import { AlertTriangle, ArrowUpCircle, HelpCircle, PauseCircle, ArrowDownCircle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { AgentUpgradeState } from '../api/clusterApi'

/**
 * Presentation of the backend agent upgrade states (Phase 1, display only).
 * The backend computes the state; the UI never compares versions.
 *
 * Severity follows the UX review: an optional update is passive and neutral
 * (blue, no urgency words); only "below the minimum" is amber, reusing the
 * "Requires agent upgrade" style of AgentSettingsCard (amber = not converged,
 * not an error; red is reserved for something the platform actually refuses,
 * and Phase 1 refuses nothing). "ok" has no tag: the version text says it.
 */
export interface UpgradePresentation {
  label: string
  className: string
  Icon: LucideIcon
  /** Tooltip / screen-reader explanation. */
  explanation: string
}

export const UPGRADE_STATE_PRESENTATION: Record<Exclude<AgentUpgradeState, 'ok'>, UpgradePresentation> = {
  outdated: {
    label: 'Update available',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    Icon: ArrowUpCircle,
    explanation: 'A newer agent version is available. Updating is optional.',
  },
  unsupported: {
    label: 'Update required',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
    Icon: AlertTriangle,
    explanation: 'This agent version is below the minimum the platform supports.',
  },
  unknown: {
    label: 'Version not reported',
    className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    Icon: HelpCircle,
    explanation: 'The agent did not report a comparable version (for example a development build), so the platform cannot tell whether it is current.',
  },
  ahead: {
    label: 'Newer than platform',
    className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    Icon: ArrowDownCircle,
    explanation: 'This agent is newer than the version the platform serves. This is normal while a release is rolling out.',
  },
}

export const PAUSED_PRESENTATION: UpgradePresentation = {
  label: 'Upgrades paused',
  className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  Icon: PauseCircle,
  explanation: 'The platform has paused agent upgrades.',
}

/** Roles that see the upgrade command, the Overview alert and the tenant banner. */
export const UPGRADE_COMMAND_ROLES = ['tenant_owner', 'tenant_admin'] as const

/** Plain-language state for tables. */
export function upgradeStateLabel(state: AgentUpgradeState): string {
  return state === 'ok' ? 'Up to date' : UPGRADE_STATE_PRESENTATION[state].label
}

/** True when an agent is behind the served version (the only states with a command). */
export function isBehind(state: AgentUpgradeState): boolean {
  return state === 'outdated' || state === 'unsupported'
}
